import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {createRole} from 'src/openapi/admin/apis/EntitlementsRoleApi';
import {useAppDispatch} from 'src/store/hooks';

import {
    DEFAULT_ROLE,

    ROLE_DESCRIPTION_CONTROL,
    ROLE_DESCRIPTION_REQUIRED_ERROR,
    ROLE_DESCRIPTION_REQUIRED_MESSAGE,

    ROLE_EXTERNAL_ID_CONTROL,
    ROLE_EXTERNAL_ID_REQUIRED_ERROR,
    ROLE_EXTERNAL_ID_REQUIRED_MESSAGE,

    ROLE_NAME_CONTROL,
    ROLE_NAME_REQUIRED_ERROR,
    ROLE_NAME_REQUIRED_MESSAGE,
} from './constants';

import type {Role} from 'src/openapi/admin/models/Role';

export const useCreateRole = () => {
    const apiDispatch = useAppDispatch();
    const {addError, removeError} = useContext(ValidationContext);

    const getDispatchAction = useCallback(() => {
        const payload: {success: true, error: false, data: Role} = {
            success: true,
            error: false,
            data: DEFAULT_ROLE,
        };
        return Promise.resolve({type: '', payload});
    }, []);

    const saveDispatchAction = useCallback((role: Role, signal: AbortSignal) => apiDispatch(
        createRole({role}, {signal}),
    ), [apiDispatch]);

    const editValidation = useCallback((data: Role) => {
        if (data.id) {
            removeError(ROLE_EXTERNAL_ID_CONTROL);
        }

        if (data.name) {
            removeError(ROLE_NAME_CONTROL);
        }

        if (data.description) {
            removeError(ROLE_DESCRIPTION_CONTROL);
        }
    }, [removeError]);

    const saveValidation = useCallback((data: Role) => {
        let valid = true;

        if (!data.id) {
            addError({
                controlId: ROLE_EXTERNAL_ID_CONTROL,
                code: ROLE_EXTERNAL_ID_REQUIRED_ERROR,
                message: ROLE_EXTERNAL_ID_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (!data.name) {
            addError({
                controlId: ROLE_NAME_CONTROL,
                code: ROLE_NAME_REQUIRED_ERROR,
                message: ROLE_NAME_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (!data.description) {
            addError({
                controlId: ROLE_DESCRIPTION_CONTROL,
                code: ROLE_DESCRIPTION_REQUIRED_ERROR,
                message: ROLE_DESCRIPTION_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        return valid;
    }, [addError]);

    return useCommonDataHook({
        initValue: DEFAULT_ROLE,
        getDispatchAction,
        saveDispatchAction,
        editValidation,
        saveValidation,
    });
};
